import { GRADIENT_MODES } from "@/constants/gradientModes";
import type { GradientMode } from "@/types/pipeParts";

const getObjectValueByPath = (obj, path) => {
  return path.split(".").reduce((prev, key) => prev && prev[key], obj);
};

export const getUniqueValues = <T extends any[]>(
  arr: T,
  key: keyof T[number]
): T[number][keyof T[number]][] => {
  const allValues = arr.map((el) => el[key]);
  return Array.from(new Set(allValues));
};

export const getMinMaxValue = <T extends any[]>(
  arr: T,
  path: GradientMode,
  round?: boolean
): [number, number] => {
  const values = arr
    .map((obj) => getObjectValueByPath(obj, path))
    .filter((el) => Number.isFinite(el));
  const gradientModesWhereMinIs0: GradientMode[] = [
    GRADIENT_MODES.OVALITY_MEAN,
    GRADIENT_MODES.OVALITY_MAX,
    GRADIENT_MODES.QUALITY,
    GRADIENT_MODES.CORROSION_PRODUCT_PERC,
  ];
  const shouldMinBe0 = gradientModesWhereMinIs0.includes(path);
  let min = shouldMinBe0 ? 0 : Math.min(...values);
  let max = Math.max(...values);

  if (round) {
    if (min !== 0) min = Math.round(min * 100 - 1) / 100;
    max = Math.round(max * 100 + 1) / 100;
  }

  return [min, max];
};

const EPSILON = 1e-9;

export const rangeFiltering = <T extends any[]>(
  arr: T,
  path: keyof T[number],
  [min, max]: [number, number] | [],
  keepMissing = true
) => {
  if (
    !arr ||
    min === undefined ||
    max === undefined ||
    !isFinite(min) ||
    !isFinite(max)
  ) {
    return arr;
  }

  return arr.filter((obj) => {
    const value = getObjectValueByPath(obj, path);

    // items without a value pass unless the caller drops them (e.g. histogram zoom)
    if (value === null || value === undefined) return keepMissing;
    return value >= (min - EPSILON) && value <= (max + EPSILON);
  });
};

export const valueFiltering = (arr, path, valueArr) => {
  if (!arr) {
    return [];
  }
  if (!valueArr || valueArr.length === 0) {
    return arr;
  }

  return arr.filter((obj) => {
    const value = getObjectValueByPath(obj, path);
    return valueArr.includes(value);
  });
};

export const findAddedAndRemovedInArray = (currentArray, prevArray) => {
  const added = currentArray.find((value) => !prevArray.includes(value));
  const removed = prevArray.find((value) => !currentArray.includes(value));

  return [added, removed];
};

export const getMaxAngle = (joints, key) => {
  let [min, max]: (string | number)[] = getMinMaxValue(joints, key);
  min = min.toFixed(2);
  max = max.toFixed(2);
  const posMin = Math.abs(Number(min));
  const posMax = Math.abs(Number(max));
  return posMin > posMax ? posMin : posMax;
};
